/**
 * CategorySidebar - Navegação por categorias
 * Sidebar com árvore de categorias e filtros de tipo
 * 
 * Versão: 1.5.0
 * - Adicionado suporte a layout mobile (prop isMobile)
 * - No mobile: largura total, sem border-right, layout compacto
 */
import React from 'react';
import { Package, Wrench, LayoutGrid, ChevronRight, Folder, FolderOpen, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { useCrmCategories, CrmProductCategory } from '@/hooks/useCrmCategories';

/**
 * Renderiza texto com highlight na parte correspondente à busca
 */
function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);
  
  if (index === -1) return <>{text}</>;
  
  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);
  
  return (
    <>
      {before}
      <mark className="bg-yellow-200 dark:bg-yellow-800 rounded px-0.5">{match}</mark>
      {after}
    </>
  );
}

/**
 * Filtra categorias recursivamente mantendo hierarquia
 * Retorna categorias que correspondem à busca ou têm filhos que correspondem
 */
function filterCategories(
  categories: CrmProductCategory[],
  query: string
): CrmProductCategory[] {
  if (!query.trim()) return categories;
  
  const lowerQuery = query.toLowerCase();
  
  return categories
    .map(category => {
      const nameMatches = category.name.toLowerCase().includes(lowerQuery);
      const filteredChildren = category.children 
        ? filterCategories(category.children, query)
        : [];
      
      // Incluir se nome corresponde OU se tem filhos que correspondem
      if (nameMatches || filteredChildren.length > 0) {
        return {
          ...category,
          children: filteredChildren.length > 0 ? filteredChildren : category.children
        };
      }
      return null;
    })
    .filter(Boolean) as CrmProductCategory[];
}

/**
 * Coleta IDs de categorias pai que contêm resultados de busca
 */
function collectMatchingParentIds(
  categories: CrmProductCategory[],
  query: string
): Set<string> {
  const ids = new Set<string>();
  if (!query.trim()) return ids;
  
  const lowerQuery = query.toLowerCase();
  
  const collect = (cats: CrmProductCategory[], parentIds: string[]) => {
    cats.forEach(cat => {
      const currentPath = [...parentIds, cat.id];
      const nameMatches = cat.name.toLowerCase().includes(lowerQuery);
      
      if (nameMatches) {
        // Adiciona todos os pais ao set
        parentIds.forEach(id => ids.add(id));
      }
      
      if (cat.children) {
        collect(cat.children, currentPath);
      }
    });
  };
  
  collect(categories, []);
  return ids;
}

// Props do componente
interface CategorySidebarProps {
  selectedCategoryId: string | null;
  onCategoryChange: (id: string | null) => void;
  productTypeFilter: 'all' | 'product' | 'service';
  onProductTypeChange: (type: 'all' | 'product' | 'service') => void;
  categoryCounts: Record<string, number>;
  totalCount: number;
  /** ID do workspace para filtrar categorias */
  workspaceId?: string | null;
  /** Modo mobile: largura total, sem border lateral */
  isMobile?: boolean;
  /** Esconde o seletor de tipo (Produto/Serviço/Todos). Usado pelo modal Nova Recorrência. */
  hideProductTypeFilter?: boolean;
}


// Props do item de categoria
interface CategoryItemProps {
  category: CrmProductCategory;
  level: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  counts: Record<string, number>;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  searchQuery: string;
}

/**
 * Item individual de categoria com suporte a hierarquia
 */
function CategoryItem({
  category,
  level,
  selectedId,
  onSelect,
  counts,
  expandedIds,
  onToggleExpand,
  searchQuery,
}: CategoryItemProps) {
  const hasChildren = category.children && category.children.length > 0;
  const isExpanded = expandedIds.has(category.id);
  const isSelected = selectedId === category.id;
  const count = counts[category.id] || 0;

  return (
    <div>
      {/* Container principal - div com role="button" para evitar botão aninhado */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(category.id)}
        onKeyDown={(e) => e.key === 'Enter' && onSelect(category.id)}
        title={category.name}
        className={cn(
          'w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md transition-colors cursor-pointer',
          'hover:bg-accent/50',
          isSelected && 'bg-accent text-accent-foreground font-medium'
        )}
        style={{ paddingLeft: `${8 + level * 12}px` }}
      >
        {/* Ícone de expansão - botão independente */}
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleExpand(category.id);
            }}
            className="p-0.5 hover:bg-muted rounded"
          >
            <ChevronRight
              className={cn(
                'h-3 w-3 transition-transform duration-200',
                isExpanded && 'rotate-90'
              )}
            />
          </button>
        ) : (
          <span className="w-4" />
        )}

        {/* Ícone de pasta com cor */}
        {isExpanded ? (
          <FolderOpen
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: category.color || 'currentColor' }}
          />
        ) : (
          <Folder
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: category.color || 'currentColor' }}
          />
        )}

        {/* Nome da categoria - com highlight de busca */}
        <span className="flex-1 text-left whitespace-nowrap">
          <HighlightText text={category.name} query={searchQuery} />
        </span>

        {/* Contador */}
        {count > 0 && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 shrink-0">
            {count}
          </Badge>
        )}
      </div>

      {/* Filhos (se expandido) */}
      {hasChildren && isExpanded && (
        <div>
          {category.children!.map((child) => (
            <CategoryItem
              key={child.id}
              category={child}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              counts={counts}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Coleta todos os IDs de categorias recursivamente
 */
function collectAllCategoryIds(categories: CrmProductCategory[]): Set<string> {
  const ids = new Set<string>();
  const collect = (cats: CrmProductCategory[]) => {
    cats.forEach(cat => {
      ids.add(cat.id);
      if (cat.children) collect(cat.children);
    });
  };
  collect(categories);
  return ids;
}

/**
 * Sidebar de navegação por categorias
 */
export function CategorySidebar({
  selectedCategoryId,
  onCategoryChange,
  productTypeFilter,
  onProductTypeChange,
  categoryCounts,
  totalCount,
  workspaceId,
  isMobile = false,
  hideProductTypeFilter = false,
}: CategorySidebarProps) {
  // Buscar categorias filtradas por workspace (se fornecido)
  const { categoryTree, isLoading } = useCrmCategories({ workspaceId });

  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = React.useState('');
  
  // Ref para controlar se a expansão inicial já foi feita
  const hasInitiallyExpanded = React.useRef(false);

  // Expandir todas as categorias apenas na primeira vez que a árvore carregar
  React.useEffect(() => {
    if (categoryTree.length > 0 && !hasInitiallyExpanded.current) {
      setExpandedIds(collectAllCategoryIds(categoryTree));
      hasInitiallyExpanded.current = true;
    }
  }, [categoryTree]);

  // Expandir automaticamente pais com resultados ao buscar
  React.useEffect(() => {
    if (searchQuery.trim() && categoryTree.length > 0) {
      const matchingParents = collectMatchingParentIds(categoryTree, searchQuery);
      setExpandedIds(prev => new Set([...prev, ...matchingParents]));
    }
  }, [searchQuery, categoryTree]);

  // Categorias filtradas pela busca
  const filteredCategories = React.useMemo(
    () => filterCategories(categoryTree, searchQuery),
    [categoryTree, searchQuery]
  );

  // Toggle expansão de categoria
  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Tipos de produto
  const typeFilters = [
    { value: 'all' as const, label: 'Todos', icon: LayoutGrid },
    { value: 'product' as const, label: 'Produtos', icon: Package },
    { value: 'service' as const, label: 'Serviços', icon: Wrench },
  ];

  return (
    <div className={cn(
      'shrink-0 flex flex-col bg-muted/20',
      isMobile 
        ? 'w-full border-b border-border/50' 
        : 'w-80 border-r border-border/50'
    )}>
      {/* Filtros de Tipo — ocultado quando hideProductTypeFilter=true (Nova Recorrência) */}
      {!hideProductTypeFilter && (
        <div className="p-2 border-b border-border/50">
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-2 px-1">
            Tipo
          </p>
          <div className="flex gap-1">
            {typeFilters.map((filter) => (
              <Button
                key={filter.value}
                variant={productTypeFilter === filter.value ? 'default' : 'ghost'}
                size="sm"
                className="flex-1 h-7 text-xs px-2"
                onClick={() => onProductTypeChange(filter.value)}
              >
                <filter.icon className="h-3 w-3" />
              </Button>
            ))}
          </div>
        </div>
      )}


      {/* Lista de Categorias */}
      <div className="flex-1 overflow-hidden">
        <div className="p-2 pb-1">
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground px-1">
            Categorias
          </p>
        </div>

        {/* Campo de busca */}
        <div className="px-2 pb-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              placeholder="Buscar categoria..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-7 text-xs pl-7 pr-7"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted rounded"
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>
        
        {/* No mobile, não usar ScrollArea fixa - o container pai já controla scroll */}
        {isMobile ? (
          <div className="p-1 space-y-0.5 min-w-max">
            {/* Opção "Todos" */}
            <button
              onClick={() => onCategoryChange(null)}
              className={cn(
                'w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md transition-colors',
                'hover:bg-accent/50',
                selectedCategoryId === null && 'bg-accent text-accent-foreground font-medium'
              )}
            >
              <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="flex-1 text-left">Todos</span>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                {totalCount}
              </Badge>
            </button>

            {isLoading ? (
              <div className="px-2 py-4 text-xs text-muted-foreground text-center">
                Carregando...
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="px-2 py-4 text-xs text-muted-foreground text-center">
                {searchQuery ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria'}
              </div>
            ) : (
              filteredCategories.map((category) => (
                <CategoryItem
                  key={category.id}
                  category={category}
                  level={0}
                  selectedId={selectedCategoryId}
                  onSelect={onCategoryChange}
                  counts={categoryCounts}
                  expandedIds={expandedIds}
                  onToggleExpand={handleToggleExpand}
                  searchQuery={searchQuery}
                />
              ))
            )}
          </div>
        ) : (
          <ScrollArea className="h-[calc(100%-70px)]" type="always">
            <div className="p-1 space-y-0.5 min-w-max">
              {/* Opção "Todos" */}
              <button
                onClick={() => onCategoryChange(null)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md transition-colors',
                  'hover:bg-accent/50',
                  selectedCategoryId === null && 'bg-accent text-accent-foreground font-medium'
                )}
              >
                <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="flex-1 text-left">Todos</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                  {totalCount}
                </Badge>
              </button>

              {/* Árvore de Categorias */}
              {isLoading ? (
                <div className="px-2 py-4 text-xs text-muted-foreground text-center">
                  Carregando...
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="px-2 py-4 text-xs text-muted-foreground text-center">
                  {searchQuery ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria'}
                </div>
              ) : (
                filteredCategories.map((category) => (
                  <CategoryItem
                    key={category.id}
                    category={category}
                    level={0}
                    selectedId={selectedCategoryId}
                    onSelect={onCategoryChange}
                    counts={categoryCounts}
                    expandedIds={expandedIds}
                    onToggleExpand={handleToggleExpand}
                    searchQuery={searchQuery}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
